'use client';

import Link from 'next/link';
import type { MouseEvent } from 'react';
import type { ComponentType } from 'react';
import { formatTimestamp } from '@/lib/task-thread-normalizer';
import {
  ArrowUpRight,
  BookOpen,
  CheckCircle2,
  CircleDot,
  CircleDotDashed,
  Clock,
  FileCode,
  FilePen,
  FolderOpen,
  FolderPlus,
  GitCommit,
  Globe,
  ListChecks,
  Loader2,
  Map,
  Move,
  Play,
  RefreshCw,
  Search,
  Sparkles,
  Trash2,
  TriangleAlert,
  Upload,
  Workflow,
  Zap,
} from 'lucide-react';

import type {
  TaskPulseEntry,
  TaskPulseEntryKind,
  TaskPulseEntryWorkspaceTarget,
  TaskPulseEvent,
  TaskPulseStage,
} from '@/types/task-thread';
import { RealityModelGlyph } from '@/components/scirix/reality-model/reality-model-mark';
import { accentVars } from '@/lib/theme/accent-utils';
import { cn } from '@/lib/utils';

export type TaskPulseCardProps = {
  event: TaskPulseEvent;
  onSelectEntry?: (taskPulseId: string, entryId: string) => void;
};

type StageVisualToken = {
  label: string;
  icon: ComponentType<{ className?: string }>;
  accent: keyof typeof accentVars;
};

const kindVisualMap: Partial<Record<TaskPulseEntryKind, StageVisualToken>> = {
  start: { label: 'Started', icon: Play, accent: 'info' },
  reading: { label: 'Reading', icon: BookOpen, accent: 'info' },
  planning: { label: 'Planning', icon: Map, accent: 'info' },
  preparing: { label: 'Preparing', icon: Workflow, accent: 'magenta' },
  executing: { label: 'Executing', icon: Zap, accent: 'success' },
  finalizing: { label: 'Finalizing', icon: Workflow, accent: 'magenta' },
  completed: { label: 'Finished', icon: CheckCircle2, accent: 'success' },
  failed: { label: 'Failed', icon: TriangleAlert, accent: 'danger' },
  canceled: { label: 'Canceled', icon: TriangleAlert, accent: 'danger' },
  paused: { label: 'Paused', icon: Workflow, accent: 'warning' },
  syncing: { label: 'Syncing', icon: RefreshCw, accent: 'magenta' },
  publishing: { label: 'Publishing', icon: Upload, accent: 'success' },
  commit: { label: 'Commit', icon: GitCommit, accent: 'success' },
  action: { label: 'Working', icon: Zap, accent: 'success' },
  step_pending: { label: 'Step', icon: CircleDotDashed, accent: 'warning' },
  step_active: { label: 'Step', icon: CircleDot, accent: 'info' },
  step_completed: { label: 'Step', icon: ListChecks, accent: 'success' },
  querying_model: { label: 'Querying Reality Model', icon: RealityModelGlyph, accent: 'magenta' },
  reading_file: { label: 'Reading File', icon: FileCode, accent: 'info' },
  writing_file: { label: 'Writing File', icon: FilePen, accent: 'success' },
  editing_file: { label: 'Editing File', icon: FilePen, accent: 'success' },
  browsing_files: { label: 'Browsing Files', icon: FolderOpen, accent: 'info' },
  deleting_file: { label: 'Deleting', icon: Trash2, accent: 'danger' },
  creating_directory: { label: 'Creating Directory', icon: FolderPlus, accent: 'success' },
  moving_file: { label: 'Moving File', icon: Move, accent: 'magenta' },
  writing_skill: { label: 'Writing Skill', icon: Sparkles, accent: 'magenta' },
  using_skill: { label: 'Using Skill', icon: Sparkles, accent: 'info' },
  deleting_skill: { label: 'Deleting Skill', icon: Trash2, accent: 'danger' },
  listing_skills: { label: 'Listing Skills', icon: Sparkles, accent: 'info' },
  fetching_url: { label: 'Fetching URL', icon: Globe, accent: 'info' },
  googling: { label: 'Googling', icon: Search, accent: 'info' },
  creating_cron: { label: 'Creating Cron Job', icon: Clock, accent: 'success' },
  updating_cron: { label: 'Updating Cron Job', icon: Clock, accent: 'magenta' },
  listing_cron: { label: 'Listing Cron Jobs', icon: Clock, accent: 'info' },
  removing_cron: { label: 'Removing Cron Job', icon: Trash2, accent: 'danger' },
  completing_cron: { label: 'Completing Cron Job', icon: CheckCircle2, accent: 'success' },
};

const stageVisualMap: Record<TaskPulseStage, StageVisualToken> = {
  reading: { label: 'Reading', icon: BookOpen, accent: 'info' },
  orchestrating: { label: 'Orchestrating', icon: Workflow, accent: 'magenta' },
  executing: { label: 'Executing', icon: Zap, accent: 'success' },
  error: { label: 'Error', icon: TriangleAlert, accent: 'danger' },
  finished: { label: 'Finished', icon: CheckCircle2, accent: 'success' },
};

function resolveVisualToken(entry: TaskPulseEntry): StageVisualToken {
  if (entry.kind) {
    const kindToken = kindVisualMap[entry.kind];
    if (kindToken) {
      return kindToken;
    }
  }
  return stageVisualMap[entry.stage];
}

type EntryStatusVisual = {
  label: string;
  accent: keyof typeof accentVars;
};

type GroupedPulseEntry = {
  representative: TaskPulseEntry;
  entries: TaskPulseEntry[];
  details: string[];
  workspaceTargets: TaskPulseEntryWorkspaceTarget[];
};

const GROUPABLE_KINDS = new Set<TaskPulseEntryKind>([
  'reading_file',
  'writing_file',
  'editing_file',
  'browsing_files',
  'deleting_file',
  'creating_directory',
  'moving_file',
  'fetching_url',
  'googling',
  'querying_model',
  'using_skill',
  'writing_skill',
  'deleting_skill',
  'listing_skills',
]);

function isGroupableKey(entry: TaskPulseEntry): string | null {
  if (!entry.kind || !GROUPABLE_KINDS.has(entry.kind)) {
    return null;
  }
  return `${entry.stage}::${entry.kind}`;
}

function groupConsecutiveEntries(entries: TaskPulseEntry[]): GroupedPulseEntry[] {
  const groups: GroupedPulseEntry[] = [];

  for (const entry of entries) {
    const key = isGroupableKey(entry);
    const previous = groups[groups.length - 1];
    const previousKey = previous ? isGroupableKey(previous.representative) : null;

    if (
      key &&
      previous &&
      previousKey === key &&
      !previous.representative.isActive &&
      !entry.isActive
    ) {
      previous.entries.push(entry);
      if (entry.detail) {
        previous.details.push(entry.detail);
      }
      if (entry.workspaceTargets?.length) {
        previous.workspaceTargets.push(...entry.workspaceTargets);
      }
      // The latest non-active entry becomes the representative so the
      // timestamp reflects the most recent action.
      previous.representative = entry;
      continue;
    }

    groups.push({
      representative: entry,
      entries: [entry],
      details: entry.detail ? [entry.detail] : [],
      workspaceTargets: entry.workspaceTargets ? [...entry.workspaceTargets] : [],
    });
  }

  return groups;
}

export function TaskPulseCard({ event, onSelectEntry }: TaskPulseCardProps) {
  const groups = groupConsecutiveEntries(event.entries);

  return (
    <article className="min-w-0 space-y-1.5" aria-label={event.taskLabel}>
      {groups.map((group) => {
        const entry = group.representative;
        const stageMeta = resolveVisualToken(entry);
        const statusMeta = resolveEntryStatus(entry);
        const groupCount = group.entries.length;
        const descriptiveLabel = formatEntryLabel(
          entry,
          stageMeta.label,
          statusMeta.label,
          groupCount,
        );

        return (
          <button
            key={entry.id}
            type="button"
            className={cn(
              'group flex min-w-0 w-full items-start gap-3 rounded-[var(--radius-lg)] border border-transparent bg-transparent px-0 py-2 text-left text-sm text-muted-foreground/90 transition',
              'hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            )}
            onClick={() => onSelectEntry?.(event.id, entry.id)}
            title={descriptiveLabel}
            aria-label={descriptiveLabel}
          >
            <StageIcon token={stageMeta} isActive={entry.isActive} />
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-muted-foreground/80">
                  {stageMeta.label}
                </span>
                <EntryStatusBadge status={statusMeta} />
                {groupCount > 1 ? (
                  <span className="text-[10px] font-semibold uppercase tracking-[0.32em] text-muted-foreground/60">
                    ×{groupCount}
                  </span>
                ) : null}
              </div>
              <p className="line-clamp-2 break-words text-sm font-medium text-foreground">{entry.summary}</p>
              <GroupedEntryDetails details={group.details} workspaceTargets={group.workspaceTargets} />
            </div>
            <div className="flex flex-col items-end">
              <span className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground/70">
                {formatTimestamp(entry.timestamp)}
              </span>
            </div>
          </button>
        );
      })}
    </article>
  );
}

type StageIconProps = {
  token: StageVisualToken;
  isActive?: boolean;
};

function StageIcon({ token, isActive }: StageIconProps) {
  const Icon = token.icon;
  const accent = accentVars[token.accent];

  return (
    <span
      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-[0px] shadow-[var(--shadow-xs)]"
      aria-hidden
      style={{
        color: accent.core,
        borderColor: accent.border,
        backgroundColor: accent.surface,
      }}
    >
      {isActive ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Icon className="h-4 w-4" />
      )}
    </span>
  );
}

type EntryStatusBadgeProps = {
  status: EntryStatusVisual;
};

function EntryStatusBadge({ status }: EntryStatusBadgeProps) {
  const accent = accentVars[status.accent];
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.32em]"
      style={{
        color: accent.foreground_muted,
        borderColor: accent.border,
        backgroundColor: accent.surface,
      }}
    >
      {status.label}
    </span>
  );
}

function resolveEntryStatus(entry: TaskPulseEvent['entries'][number]): EntryStatusVisual {
  if (entry.stage === 'error') {
    return { label: 'Error', accent: 'danger' };
  }

  if (entry.isActive) {
    return { label: 'In progress', accent: 'info' };
  }

  // Step entries have a planning lifecycle (pending → active → completed) that
  // is independent of the visual stage. Render them with explicit lifecycle
  // labels so "completed" doesn't get rendered as the misleading "Stopped".
  if (entry.kind === 'step_completed') {
    return { label: 'Done', accent: 'success' };
  }
  if (entry.kind === 'step_pending') {
    return { label: 'Pending', accent: 'warning' };
  }

  if (entry.stage === 'finished') {
    return { label: 'Finished', accent: 'success' };
  }

  // Any other historical entry (action queue, past state transitions, commits)
  // already happened by the time we render it, so it's "Done", not "Waiting".
  return { label: 'Done', accent: 'success' };
}

function formatEntryLabel(
  entry: TaskPulseEvent['entries'][number],
  stageLabel: string,
  statusLabel: string,
  groupCount = 1,
) {
  const detail = entry.detail ? ` · ${entry.detail}` : '';
  const count = groupCount > 1 ? ` · ×${groupCount}` : '';
  return `${stageLabel} (${statusLabel}) · ${entry.summary}${detail}${count} · ${formatTimestamp(entry.timestamp)}`;
}

type GroupedEntryDetailsProps = {
  details: string[];
  workspaceTargets: TaskPulseEntryWorkspaceTarget[];
};

function GroupedEntryDetails({ details, workspaceTargets }: GroupedEntryDetailsProps) {
  if (details.length === 0) {
    return null;
  }

  // Single detail without "·" separators is rendered as plain prose so legacy
  // commit/state copy continues to look the same.
  if (details.length === 1 && !details[0].includes('·')) {
    const normalized = details[0];
    if (!resolveWorkspaceLink(normalized, workspaceTargets)) {
      return <p className="line-clamp-2 break-all text-xs font-mono text-muted-foreground/80">{normalized}</p>;
    }

    return (
      <div className="flex flex-col gap-0.5">
        <EntryDetailToken token={normalized} workspaceTargets={workspaceTargets} />
      </div>
    );
  }

  // De-duplicate while preserving order so repeated reads of the same file
  // collapse to a single token.
  const seen = new Set<string>();
  const tokens: string[] = [];
  for (const detail of details) {
    const parts = detail
      .split('·')
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
    for (const part of parts) {
      if (seen.has(part)) {
        continue;
      }
      seen.add(part);
      tokens.push(part);
    }
  }

  return (
    <div className="flex flex-col gap-0.5">
      {tokens.map((token, index) => (
        <EntryDetailToken key={`${token}-${index}`} token={token} workspaceTargets={workspaceTargets} />
      ))}
    </div>
  );
}

type WorkspaceLinkTarget = {
  href: string;
  kind: 'document' | 'file' | 'review';
  label: string;
};

type EntryDetailTokenProps = {
  token: string;
  workspaceTargets: TaskPulseEntryWorkspaceTarget[];
};

function EntryDetailToken({ token, workspaceTargets }: EntryDetailTokenProps) {
  const workspaceLink = resolveWorkspaceLink(token, workspaceTargets);

  if (!workspaceLink) {
    return (
      <span
        className="flex max-w-full items-center truncate rounded bg-muted px-2 py-0.5 text-[11px] font-mono font-medium text-muted-foreground"
        title={token}
      >
        {token}
      </span>
    );
  }

  const handleWorkspaceLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
  };

  return (
    <Link
      href={workspaceLink.href}
      prefetch={false}
      onClick={handleWorkspaceLinkClick}
      className="group flex max-w-full items-center gap-1 truncate rounded bg-muted px-2 py-0.5 text-[11px] font-mono font-medium text-muted-foreground transition hover:bg-muted/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      title={describeWorkspaceLink(workspaceLink)}
    >
      <span className="truncate">{workspaceLink.label}</span>
      <ArrowUpRight className="h-3 w-3 shrink-0 text-muted-foreground/80 group-hover:text-foreground" aria-hidden />
    </Link>
  );
}

const DOCUMENTS_WORKSPACE_PATH = '/documents';
const FILES_WORKSPACE_PATH = '/files';

function resolveWorkspaceLink(
  rawToken: string,
  workspaceTargets: TaskPulseEntryWorkspaceTarget[],
): WorkspaceLinkTarget | null {
  const normalized = normalizeWorkspacePathToken(rawToken);
  if (!normalized) {
    return null;
  }

  // Prefer explicit workspace targets emitted by the server: they carry the
  // namespace, and for review-mode entries the base/head branches needed to
  // link into the Files workspace diff view. The target's path is
  // repo-relative while the display token may include the repo-name prefix
  // (agents work in an aggregated workspace), so accept either an exact
  // match or a suffix match separated by `/`.
  const pathTarget = workspaceTargets.find((target) => {
    if (typeof target.path !== 'string' || target.path.length === 0) return false;
    if (target.path === normalized) return true;
    return normalized.endsWith(`/${target.path}`);
  });
  if (pathTarget) {
    return buildTargetLink(pathTarget, pathTarget.path ?? normalized);
  }

  const branchTarget = workspaceTargets.find(
    (target) =>
      typeof target.headBranch === 'string' && target.headBranch === normalized,
  );
  if (branchTarget && branchTarget.baseBranch && branchTarget.headBranch) {
    return {
      href: buildFilesReviewHref(branchTarget, undefined),
      kind: 'review',
      label: normalized,
    };
  }

  // Fallback: no target match. Keep the legacy path-token heuristic so plain
  // file paths still resolve without server-side metadata.
  if (isDocumentPath(normalized)) {
    return {
      href: buildSimpleHref(DOCUMENTS_WORKSPACE_PATH, 'docPath', normalized),
      kind: 'document',
      label: normalized,
    };
  }

  if (isFilePath(normalized)) {
    return {
      href: buildSimpleHref(FILES_WORKSPACE_PATH, 'path', normalized),
      kind: 'file',
      label: normalized,
    };
  }

  return null;
}

function buildTargetLink(
  target: TaskPulseEntryWorkspaceTarget,
  normalizedPath: string,
): WorkspaceLinkTarget {
  // Review-mode wins over tree/documents links. The user is reviewing branch
  // changes; sending them to the documents workspace (which serves the default
  // branch) would show the pre-edit copy.
  if (target.baseBranch && target.headBranch) {
    return {
      href: buildFilesReviewHref(target, normalizedPath),
      kind: 'review',
      label: normalizedPath,
    };
  }

  if (target.kind === 'documents') {
    return {
      href: buildSimpleHref(DOCUMENTS_WORKSPACE_PATH, 'docPath', normalizedPath),
      kind: 'document',
      label: normalizedPath,
    };
  }

  return {
    href: buildSimpleHref(FILES_WORKSPACE_PATH, 'path', normalizedPath),
    kind: 'file',
    label: normalizedPath,
  };
}

function buildFilesReviewHref(
  target: TaskPulseEntryWorkspaceTarget,
  path: string | undefined,
) {
  const params = new URLSearchParams();
  params.set('base', target.baseBranch ?? '');
  params.set('head', target.headBranch ?? '');
  if (path) {
    params.set('path', path);
  }
  // Namespace lives on the path in the new routing (`/{slug}/files`). Fall
  // back to the flat path when no slug is available — better than building a
  // URL that 404s with the GUID in the slot.
  const base = target.namespaceSlug
    ? `/${encodeURIComponent(target.namespaceSlug)}/files`
    : FILES_WORKSPACE_PATH;
  return `${base}?${params.toString()}`;
}

function describeWorkspaceLink(link: WorkspaceLinkTarget) {
  switch (link.kind) {
    case 'document':
      return `Open Documents workspace at ${link.label}`;
    case 'review':
      return `Open Files workspace review for ${link.label}`;
    default:
      return `Open Files workspace at ${link.label}`;
  }
}

function normalizeWorkspacePathToken(token: string) {
  const trimmed = token.trim().replace(/^[`'"“”]+|[`'"“”]+$/g, '');
  if (!trimmed) {
    return null;
  }

  const normalized = trimmed.replace(/\\/g, '/');
  return normalized;
}

function isDocumentPath(path: string) {
  if (!path.includes('/')) {
    return false;
  }
  return /\.(md|mdx)$/i.test(path);
}

function isFilePath(path: string) {
  if (!path.includes('/')) {
    return false;
  }

  if (path.includes('://')) {
    return false;
  }

  return true;
}

function buildSimpleHref(basePath: string, paramName: string, path: string) {
  const searchParams = new URLSearchParams({ [paramName]: path });
  return `${basePath}?${searchParams.toString()}`;
}
